'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { FieldErrors, FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import InputWithExample from '@/components/widget/input-with-example'
import { getStepUpMode } from '@/lib/step-up'
import { toast } from '@/lib/toast'
import { useSendStepUpCode } from '@/service/step-up.hook'
import { SensitiveAction } from '@/types/generated/common/auth'

// Flat field set every consumer form must carry so this subform can register
// onto it. The backend `verification` oneof accepts exactly one of totp/backup/
// email; the submit handler forwards whichever is populated.
export interface StepUpFieldValues extends FieldValues {
  currentPassword?: string
  totpCode?: string
  backupCode?: string
  emailCode?: string
}

// Cosmetic only — the real 3/hour budget is enforced server-side.
const SEND_CODE_COOLDOWN_SECONDS = 30

const OtpSlots = () => (
  <div className="flex items-center gap-2">
    <InputOTPGroup className="gap-2">
      <InputOTPSlot className="w-8" index={0} />
      <InputOTPSlot className="w-8 border-l" index={1} />
      <InputOTPSlot className="w-8 border-l" index={2} />
    </InputOTPGroup>
    <InputOTPSeparator />
    <InputOTPGroup className="gap-2">
      <InputOTPSlot className="w-8" index={3} />
      <InputOTPSlot className="w-8 border-l" index={4} />
      <InputOTPSlot className="w-8 border-l" index={5} />
    </InputOTPGroup>
  </div>
)

export default function StepUpVerificationFields<T extends StepUpFieldValues>({
  form,
  hasPassword,
  totpEnabled,
  action,
}: {
  form: UseFormReturn<T>
  hasPassword: boolean
  totpEnabled: boolean
  action: SensitiveAction
}) {
  const t = useTranslations('settings')
  const mode = getStepUpMode(totpEnabled)

  // Field names are a fixed, known set; cast once to this form's Path type.
  const currentPasswordName = 'currentPassword' as Path<T>
  const totpCodeName = 'totpCode' as Path<T>
  const backupCodeName = 'backupCode' as Path<T>
  const emailCodeName = 'emailCode' as Path<T>
  const errors = form.formState.errors as FieldErrors<StepUpFieldValues>

  const [codeSent, setCodeSent] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  const { mutateAsync: sendCode, isPending: isSending } = useSendStepUpCode({
    onSuccess: () => {
      setCodeSent(true)
      setCooldown(SEND_CODE_COOLDOWN_SECONDS)
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000)
    return () => clearInterval(id)
  }, [cooldown])

  const handleSendCode = async () => {
    // onError already toasts; swallow the rejection so it isn't unhandled.
    await sendCode({ action }).catch(() => {})
  }

  // Backup codes are the lost-device fallback, so they're hidden until asked for. Switching
  // clears the other field: the submit forwards whichever one is filled.
  const [useBackup, setUseBackup] = useState(false)
  const showBackup = useBackup || Boolean(errors.backupCode?.message)
  const toggleBackup = (next: boolean) => {
    setUseBackup(next)
    form.clearErrors([totpCodeName, backupCodeName])
    form.setValue(next ? totpCodeName : backupCodeName, '' as PathValue<T, Path<T>>)
  }

  return (
    <>
      {hasPassword && (
        <div className="flex flex-col gap-1">
          <Label className="text-sm tracking-tight">{t('currentPassword')}</Label>
          <Input
            type="password"
            placeholder={t('currentPassword')}
            {...form.register(currentPasswordName)}
            error={errors.currentPassword?.message}
          />
        </div>
      )}

      {mode === 'totp' ? (
        <div className="flex flex-col gap-1">
          <Label className="text-sm tracking-tight">
            {showBackup ? t('backupCode') : t('twoFactorCode')}
          </Label>
          {showBackup ? (
            <InputWithExample
              example="0000-0000"
              placeholder={t('backupCode')}
              {...form.register(backupCodeName)}
              error={errors.backupCode?.message}
            />
          ) : (
            <>
              <InputOTP
                maxLength={6}
                value={(form.watch(totpCodeName) as string) ?? ''}
                onChange={(value) => form.setValue(totpCodeName, value as PathValue<T, Path<T>>)}
              >
                <OtpSlots />
              </InputOTP>
              {errors.totpCode?.message && (
                <p className="text-destructive text-sm">{errors.totpCode.message}</p>
              )}
            </>
          )}
          {/* The fallback stays out of the way: a quiet link, not a second field. */}
          <button
            type="button"
            className="text-foreground/50 hover:text-foreground/80 mt-1 cursor-pointer self-end text-xs tracking-tight transition-colors"
            onClick={() => toggleBackup(!showBackup)}
          >
            {showBackup ? t('useAuthenticatorCode') : t('useBackupCode')}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <Label className="text-sm tracking-tight">{t('emailCode')}</Label>
          <p className="text-foreground/60 text-xs tracking-tight">{t('emailCodeDescription')}</p>
          <div className="mt-1 flex items-center justify-between gap-2">
            <InputOTP
              maxLength={6}
              disabled={!codeSent}
              value={(form.watch(emailCodeName) as string) ?? ''}
              onChange={(value) => form.setValue(emailCodeName, value as PathValue<T, Path<T>>)}
            >
              <OtpSlots />
            </InputOTP>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isSending || cooldown > 0}
              onClick={handleSendCode}
            >
              {cooldown > 0
                ? t('resendCodeIn', { seconds: cooldown })
                : codeSent
                  ? t('resendCode')
                  : t('sendCode')}
            </Button>
          </div>
          {errors.emailCode?.message && (
            <p className="text-destructive text-sm">{errors.emailCode.message}</p>
          )}
        </div>
      )}
    </>
  )
}
